import { ApiError, get, patch, post, del } from "aws-amplify/api";
import { Level, Logger } from "./logger";

function amplifyErrorHandler(
  action: string,
  error: any,
  logger: Logger,
): never {
  let logLevel: Level = "ERROR";

  if (error instanceof ApiError) {
    if (error.response) {
      const { statusCode } = error.response;
      if (statusCode === 401) {
        // Unauthorized probably means the token expired.
        logLevel = "WARN";
      }
    }
  }
  switch (logLevel) {
    case "WARN":
      logger.warn(`${action} failed`, { error, metadata: { action } });
      break;
    case "ERROR":
      logger.error(`${action} failed`, { error, metadata: { action } });
      break;
    default:
      break;
  }
  throw error;
}

type AmplifyRequestOptions = {
  headers?: { [key: string]: string };
  queryStringParameters?: { [key: string]: string };
  body?: any;
};

/**
 * Wrapper around aws-amplify's get method that manages logging
 *
 * NOTE: Does NOT swallow errors! Consumers are still responsible for deciding
 * application behavior when a network request fails
 *
 * @example
 * ```ts
 * getUserProfile(hybridToken: string) {
 *   return AmplifyGet<{ userProfile: UserProfile }>(
 *     'Get User Profile',
 *     'SalesExp',
 *     '/getUserProfile',
 *     {
 *       headers: {
 *         Authorization: `Bearer ${hybridToken}`,
 *       },
 *     },
 *     logger,
 *   );
 * }
 * ```
 */
function AmplifyGet<T>(
  action: string,
  apiName: string,
  path: string,
  options: AmplifyRequestOptions,
  logger: Logger,
) {
  return get({
    apiName,
    path,
    options: {
      body: options.body,
      headers: options.headers,
      queryParams: options.queryStringParameters,
    },
  })
    .response.then((res) => res.body.json())
    .then((res) => res as T)
    .catch((error) => {
      amplifyErrorHandler(action, error, logger);
    });
}

/**
 * Wrapper around aws-amplify's patch method that manages logging
 *
 * NOTE: Does NOT swallow errors! Consumers are still responsible for deciding
 * application behavior when a network request fails
 *
 * @example
 * ```ts
 * tableauLogin(email: string): Promise<any> {
 *   return AmplifyPatch<{ credentials: { token: string } }>(
 *     'Update Prospect',
 *     'SalesExpLeadsApi',
 *    `/prospect/${prospectId}`,
 *    {
 *      headers: {
 *        Authorization: `Bearer ${authKey}`,
 *      },
 *      body: {...},
 *     }
 *     logger,
 *   );
 * }
 * ```
 */
function AmplifyPatch<T>(
  action: string,
  apiName: string,
  path: string,
  options: AmplifyRequestOptions,
  logger: Logger,
) {
  return patch({
    apiName,
    path,
    options,
  })
    .response.then((res) => res.body.json())
    .then((res) => res as T)
    .catch((error) => {
      amplifyErrorHandler(action, error, logger);
    });
}

/**
 * Wrapper around aws-amplify's post method that manages logging
 *
 * NOTE: Does NOT swallow errors! Consumers are still responsible for deciding
 * application behavior when a network request fails
 *
 * @example
 * ```ts
 * tableauLogin(email: string): Promise<any> {
 *   return AmplifyPost<{ credentials: { token: string } }>(
 *     'Tableau Login',
 *     'SalesExp',
 *     '/tableau/login',
 *    {
 *      headers: {
 *        Authorization: `Bearer ${authKey}`,
 *      },
 *       body: { userEmail: email },
 *     },
 *     logger,
 *   );
 * }
 * ```
 */
function AmplifyPost<T>(
  action: string,
  apiName: string,
  path: string,
  options: AmplifyRequestOptions,
  logger: Logger,
) {
  return post({
    apiName,
    path,
    options,
  })
    .response.then((res) => res.body.json())
    .then((res) => res as T)
    .catch((error) => {
      amplifyErrorHandler(action, error, logger);
    });
}

/**
 * Wrapper around aws-amplify's delete method that manages logging
 *
 * NOTE: Does NOT swallow errors! Consumers are still responsible for deciding
 * application behavior when a network request fails
 *
 * @example
 * ```ts
 * deleteProposal(email: string): Promise<any> {
 *   return AmplifyDelete(
 *     'Delete Proposal',
 *     'SalesExp',
 *     '/proposals?proposalId=${proposalId}',
 *    {
 *      headers: {
 *        Authorization: `Bearer ${authKey}`,
 *      },
 *    }
 *     logger,
 *   );
 * }
 * ```
 */
function AmplifyDelete<T>(
  action: string,
  apiName: string,
  path: string,
  options: AmplifyRequestOptions,
  logger: Logger,
) {
  return del({
    apiName,
    path,
    options,
  })
    .response.then((res) => res as T)
    .catch((error) => {
      amplifyErrorHandler(action, error, logger);
    });
}

export { AmplifyGet, AmplifyPatch, AmplifyPost, AmplifyDelete };
